import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { DecideDialog } from "@/components/claims/DecideDialog";
import { SelfResolveDialog } from "@/components/claims/SelfResolveDialog";
import { ClaimCard, CLAIM_TYPE_STYLE } from "@/components/claims/ClaimCard";
import { NewClaimDialog } from "@/components/claims/NewClaimDialog";
import { Button } from "@/components/ui/button";

export { CLAIM_TYPE_STYLE };

// Folders — Open first, then one per decision, Resolved (replacement fulfilled), All.
const FOLDERS = [
  ["open", "Open"], ["refund", "Refunds"], ["warranty", "Warranty"], ["reorder", "Re-ordered"],
  ["new_logo", "New logo"], ["declined", "Declined / Resolved"], ["resolved", "Resolved"], ["all", "All"],
];
const FOLDER_KEYS = new Set(FOLDERS.map(([k]) => k));

export default function Claims() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("claim") || "";
  const [tab, setTab] = useState("open");
  const [decide, setDecide] = useState(null);
  const [selfResolve, setSelfResolve] = useState(null);
  const [newClaimOpen, setNewClaimOpen] = useState(false);
  // Deep link /claims?claim=<id> (order chip, claim banner, alerts): open the claim's folder, scroll to its card.
  const { data: focusData } = useQuery({
    queryKey: ["claim-focus", focusId],
    queryFn: async () => (await api.get(`/claims?claim_id=${focusId}`)).data,
    enabled: !!focusId,
  });
  useEffect(() => {
    const st = focusData?.claims?.[0]?.status;
    if (st) setTab(FOLDER_KEYS.has(st) ? st : "all");
  }, [focusData]);
  const { data, isLoading } = useQuery({
    queryKey: ["claims", tab],
    queryFn: async () => (await api.get(`/claims?status=${tab}`)).data,
    refetchInterval: 30_000,
  });
  const claims = data?.claims || [];
  useEffect(() => {
    if (!focusId || !claims.some((c) => c.id === focusId)) return;
    const el = document.getElementById(`claim-${focusId}`);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    const tmr = setTimeout(() => setSearchParams({}, { replace: true }), 8000);
    return () => clearTimeout(tmr);
  }, [focusId, claims, setSearchParams]);
  const counts = data?.counts || {};
  const canDecide = !!data?.can_decide;
  const canMailLogo = user?.role === "back_office" || user?.role === "super_admin";
  const canSelfResolve = !!data?.can_file;
  const canSendLogo = user?.role === "back_office";
  return (
    <div className="p-6 space-y-4" data-testid="claims-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">⚠️ {t("Claims")} <span className="text-slate-400 font-normal text-base">· {t("After-sale QC")}</span></h1>
          <p className="text-sm text-slate-500">
            {canDecide
              ? t("Decide each claim: warranty replacement, paid re-order, refund or declined/resolved. Your solution note is shown to Back Office.")
              : t("Claims you filed on fulfilled orders — Factory One / Super Admin decide. Decided claims are filed by folder below.")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canSelfResolve && (
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setNewClaimOpen(true)} data-testid="new-claim-btn">
              ＋ {t("New claim")}
            </Button>
          )}
          <div className="inline-flex flex-wrap rounded-md border border-slate-300 overflow-hidden text-sm" data-testid="claims-tabs">
            {FOLDERS.map(([k, l]) => {
              const n = k === "all" ? null : counts[k];
              return (
                <button key={k} type="button" onClick={() => setTab(k)} data-testid={`claims-tab-${k}`}
                        className={`px-3 py-1.5 font-semibold ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  {t(l)}{n ? ` (${n})` : ""}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {canSelfResolve && <NewClaimDialog open={newClaimOpen} onOpenChange={setNewClaimOpen} t={t} />}
      {isLoading && <div className="text-slate-500 text-sm">{t("Loading…")}</div>}
      {!isLoading && claims.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="claims-empty">{t("No claims here.")}</div>
      )}
      <div className="space-y-3">
        {claims.map((c) => (
          <ClaimCard key={c.id} c={c} canDecide={canDecide} canMailLogo={canMailLogo} canSelfResolve={canSelfResolve} canSendLogo={canSendLogo} lang={lang} t={t} focused={c.id === focusId}
                     onDecide={(cl, a) => setDecide({ claim: cl, action: a })} onSelfResolve={(cl, a) => setSelfResolve({ claim: cl, action: a })} />
        ))}
      </div>
      {decide && <DecideDialog claim={decide.claim} action={decide.action} onClose={() => setDecide(null)} />}
      {selfResolve && <SelfResolveDialog claim={selfResolve.claim} action={selfResolve.action} onClose={() => setSelfResolve(null)} />}
      {user?.role === "back_office" && <div className="text-[11px] text-slate-400">{t("Factory One and Super Admin decide; you handle return labels and new logos.")}</div>}
    </div>
  );
}
